import * as THREE from "three";

class PathTracer {
  constructor(object, path = null, lookTarget = null) {
    this.object = object;
    this.path = path;
    this.lookTarget = lookTarget;

    this.speed = 10;
    this.pathLength = path ? path.getLengths().at(-1) : 1;
    this.speedNormalizeFactor = 1 / this.pathLength;
    this.repeat = null;
    this.paused = false;
    this.enableRotation = true;
    this.enableLookAt = true;

    this.currentPoint = 0;
    this.lastPoint = 0;
    this.nextPoint = 0;

    this.actionTimer = null;
    this.actionType = "DEFAULT";
    this.moveDirection = "FORWARD";
    this.rotationOptions = "ALONG";
    this.lookOptions = "TARGETXZ";

    this.up = new THREE.Vector3(0, 1, 0);
    this.axisX = new THREE.Vector3(1, 0, 0);
    this.axis = new THREE.Vector3();
    this.tangent = new THREE.Vector3();
    this.currentPosition = new THREE.Vector3();
  }

  setAction(options) {
    switch (options) {
      case "DEFAULT":
        return;
      case "RANDOM":
        if (this.actionTimer) return;
        const rand = Math.floor(Math.random() * 10);

        this.actionTimer = setTimeout(() => {
          this.actionTimer = null;
        }, 1000);

        if (rand === 7) {
          if (this.moveDirection === "FORWARD") {
            this.moveDirection = "BACKWARD";
          } else {
            this.moveDirection = "FORWARD";
          }
        }
        if (rand === 8) {
          this.pause();
        }
        if (rand === 9) {
          this.speed = Math.floor(Math.random() * 10);
        }
        break;
      default:
        break;
    }
  }

  setRotation(options) {
    if (!this.object.rotation || !this.enableRotation) return;

    let radians;
    switch (options) {
      case "ALONG":
        this.tangent = this.path.getTangent(this.currentPoint);
        this.axis.crossVectors(this.up, this.tangent).normalize();

        radians = Math.acos(this.up.dot(this.tangent));
        this.object.quaternion.setFromAxisAngle(this.axis, radians);
        break;
      case "FIXEDXZ":
        this.tangent = this.path.getTangent(this.currentPoint);

        const tangentXZ = new THREE.Vector3(
          this.tangent.x,
          0,
          this.tangent.z,
        ).normalize();
        radians = Math.acos(tangentXZ.dot(this.axisX));
        if (this.tangent.z < 0) {
          radians += Math.PI / 2;
        }
        this.object.quaternion.setFromAxisAngle(this.up, radians);
        break;
      default:
        break;
    }
  }

  setLookAt(options) {
    if (!this.object.lookAt || !this.enableLookAt) return;

    switch (options) {
      case "LAST":
        if (!this.path) return;
        this.object.lookAt(this.lastPosition);
        break;
      case "NEXT":
        if (!this.path) return;
        this.object.lookAt(this.nextPosition);
        break;
      case "TARGET":
        this.object.lookAt(this.lookTarget.position);
        break;
      case "TARGETXZ":
        this.object.lookAt(
          this.lookTarget.position.x,
          this.object.position.y,
          this.lookTarget.position.z,
        );
        break;
      default:
        this.object.lookAt(this.lastPosition);
        break;
    }
  }

  update(dt) {
    if (this.path === null) {
      this.setLookAt(this.lookOptions);
      return;
    }

    if (this.paused === true || this.repeat === 0) {
      this.setAction(this.actionType);
      this.setRotation(this.rotationOptions);
      this.setLookAt(this.lookOptions);
      return;
    }

    const delta = dt * this.speed * this.speedNormalizeFactor;

    if (this.moveDirection === "FORWARD") {
      this.currentPoint += delta;
      this.lastPoint = this.currentPoint - delta;
      this.nextPoint = this.currentPoint + delta;

      if (this.currentPoint > 1) {
        if (this.repeat !== null) {
          this.currentPoint = 0;
          this.lastPoint = 0;
          this.repeat -= 1;
        } else {
          this.currentPoint = 0;
          this.lastPoint = 0;
        }
      }
      if (this.nextPoint > 1) {
        this.nextPoint = 1;
      }
      if (this.lastPoint < 0) {
        this.lastPoint = 0;
      }
    } else if (this.moveDirection === "BACKWARD") {
      this.currentPoint -= delta;
      this.lastPoint = this.currentPoint + delta;
      this.nextPoint = this.currentPoint - delta;

      if (this.currentPoint < 0) {
        if (this.repeat !== null) {
          this.currentPoint = 1;
          this.lastPoint = 1;
          this.repeat -= 1;
        } else {
          this.currentPoint = 1;
          this.lastPoint = 1;
        }
      }
      if (this.nextPoint < 0) {
        this.nextPoint = 0;
      }
      if (this.lastPoint > 1) {
        this.lastPoint = 1;
      }
    }
    this.lastPosition = this.path.getPoint(this.lastPoint);
    this.currentPosition = this.path.getPoint(this.currentPoint);
    this.nextPosition = this.path.getPoint(this.nextPoint);

    this.object.position.copy(this.currentPosition);

    this.setAction(this.actionType);
    this.setRotation(this.rotationOptions);
    this.setLookAt(this.lookOptions);
  }

  pause() {
    this.paused = !this.paused;
  }
}

export default PathTracer;
